import { useEffect, useState } from 'react';
import { Pagination } from '@/components/Pagination';
import { Input } from '@/components/Input';
import { StatusPill } from '@/components/StatusPill';
import { LoadErrorState } from '@/components/ListStates';
import { Button } from '@/components/Button';
import { SettingsCard, SettingsPage } from '@/components/settings/SettingsPage';
import { settingsService, AuditEntry } from '@/services/settings.service';
import { Activity, Search, ChevronDown, ChevronUp } from 'lucide-react';
import clsx from 'clsx';

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const secs = Math.floor(diff / 1000);
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

const SIMPLE_VERBS = new Set(['created', 'updated', 'deleted', 'archived', 'activated', 'deactivated', 'connected', 'disconnected', 'suspended', 'restored', 'sent', 'imported']);

/** "department.created" → "Department created", "settings.team_user_updated" → "Team user updated". */
function humanAction(action: string): string {
  const parts = action.split('.');
  const last = parts[parts.length - 1] ?? action;
  const text = SIMPLE_VERBS.has(last) && parts.length > 1
    ? `${parts[parts.length - 2].replace(/[_-]/g, ' ')} ${last}`
    : last.replace(/[_-]/g, ' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function roleVariant(role: string): 'info' | 'warning' | 'neutral' {
  if (role === 'company_admin') return 'info';
  if (role === 'super_admin') return 'warning';
  return 'neutral';
}

const ExpandedRow: React.FC<{ entry: AuditEntry }> = ({ entry }) => (
  <div className="bg-surface-alt border-b border-border px-5 py-4 text-xs space-y-2">
    <div className="grid gap-4 @md:grid-cols-2">
      {entry.ip && (
        <div>
          <div className="text-2xs font-semibold uppercase tracking-[0.14em] text-text-muted mb-1">IP Address</div>
          <div className="font-mono text-text-secondary">{entry.ip}</div>
        </div>
      )}
      {entry.userAgent && (
        <div>
          <div className="text-2xs font-semibold uppercase tracking-[0.14em] text-text-muted mb-1">User Agent</div>
          <div className="text-text-muted break-all">{entry.userAgent}</div>
        </div>
      )}
    </div>
    {entry.payload && Object.keys(entry.payload).length > 0 && (
      <div>
        <div className="text-2xs font-semibold uppercase tracking-[0.14em] text-text-muted mb-1">Payload</div>
        <pre className="rounded-lg bg-surface border border-border p-3 font-mono text-[11px] text-text-secondary overflow-x-auto whitespace-pre-wrap">
          {JSON.stringify(entry.payload, null, 2)}
        </pre>
      </div>
    )}
  </div>
);

// Container widths, not the viewport: at 1024 px the page is ~460 px wide, so target and role fold under the action.
const ROW = 'grid grid-cols-[84px_minmax(0,1fr)_20px] gap-3 @2xl:grid-cols-[112px_minmax(0,1.6fr)_minmax(0,1fr)_96px_28px] @2xl:gap-4';

export default function ActivitySettings() {
  const [data, setData] = useState<{ items: AuditEntry[]; total: number; page: number; limit: number } | null>(null);
  const [page, setPage] = useState(1);
  const [actionFilter, setActionFilter] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoadError(false);
    settingsService
      .activity({ page, limit: 50, action: actionFilter || undefined })
      .then((r) => { if (alive) setData(r); })
      .catch(() => { if (alive) setLoadError(true); });
    return () => { alive = false; };
  }, [page, actionFilter, reloadKey]);

  const clearFilter = () => { setActionFilter(''); setPage(1); };

  const toggleRow = (id: string) => {
    setExpanded((prev) => (prev === id ? null : id));
  };

  return (
    <SettingsPage
      eyebrow="Account"
      title="Activity log"
      description="Every change made in this workspace — settings, team, connections and records — and who made it. Open a row for the details."
    >
      <div className="space-y-4 py-6">
      <div className="flex flex-wrap items-center gap-3">
        <div className="w-full sm:w-72">
          <Input
            value={actionFilter}
            onChange={(e) => {
              setActionFilter(e.target.value);
              setPage(1);
            }}
            placeholder="Filter by action…"
            iconLeft={<Search size={14} />}
          />
        </div>
        {data && (
          <span className="text-xs text-text-muted tabular-nums">
            {data.total.toLocaleString()} {data.total === 1 ? 'entry' : 'entries'}
          </span>
        )}
      </div>

      <SettingsCard padded={false}>
        {loadError ? (
          <LoadErrorState bare what="the audit log" onRetry={() => setReloadKey((k) => k + 1)} />
        ) : data === null ? (
          <div className="space-y-2 p-5">
            {[...Array(5)].map((_, i) => <div key={i} className="skeleton h-12 rounded-xl" />)}
          </div>
        ) : data.items.length === 0 && actionFilter.trim() ? (
          <div className="flex flex-col items-center gap-3 p-14 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-soft text-primary">
              <Search size={24} />
            </div>
            <div>
              <div className="text-sm font-semibold text-text-primary">Nothing matches your filters</div>
              <div className="mt-1 text-xs text-text-muted">No audit entries match “{actionFilter.trim()}”.</div>
            </div>
            <Button variant="secondary" size="sm" onClick={clearFilter}>Clear filters</Button>
          </div>
        ) : data.items.length === 0 ? (
          <div className="flex flex-col items-center gap-3 p-14 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-soft text-primary">
              <Activity size={24} />
            </div>
            <div>
              <div className="text-sm font-semibold text-text-primary">No activity yet</div>
              <div className="mt-1 text-xs text-text-muted">Changes made in your workspace — settings, team, connections and records — are logged here.</div>
            </div>
          </div>
        ) : (
          <div>
            {/* Header */}
            <div className={clsx(ROW, 'hidden border-b border-border bg-surface-alt/50 px-5 py-2.5 text-2xs font-semibold uppercase tracking-[0.14em] text-text-muted @2xl:grid')}>
              <span>When</span>
              <span>Action</span>
              <span>Target</span>
              <span>Actor role</span>
              <span />
            </div>

            {data.items.map((entry) => (
              <div key={entry._id}>
                <button
                  onClick={() => toggleRow(entry._id)}
                  className={clsx(
                    ROW, 'w-full items-center border-b border-border px-5 py-3 text-left transition-colors',
                    expanded === entry._id
                      ? 'bg-surface-alt'
                      : 'hover:bg-surface-alt',
                  )}
                >
                  {/* Timestamp */}
                  <div>
                    <div
                      className="text-sm font-medium text-text-primary"
                      title={new Date(entry.createdAt).toLocaleString()}
                    >
                      {relativeTime(entry.createdAt)}
                    </div>
                    <div className="text-xs text-text-muted">
                      {new Date(entry.createdAt).toLocaleDateString()}
                    </div>
                  </div>

                  {/* Action */}
                  <div className="min-w-0" title={entry.action}>
                    <div className="flex items-center gap-1.5">
                      <span className="truncate text-sm font-medium text-text-primary">{humanAction(entry.action)}</span>
                      {entry.outcome === 'failed' && (
                        <span className="shrink-0 rounded-full bg-danger-soft px-1.5 py-0.5 text-[10px] font-semibold text-danger">
                          Failed{entry.statusCode ? ` · ${entry.statusCode}` : ''}
                        </span>
                      )}
                    </div>
                    <div className="truncate font-mono text-[11px] text-text-muted">{entry.action}</div>
                    <div className="mt-1 flex min-w-0 flex-wrap items-center gap-2 text-xs text-text-secondary @2xl:hidden">
                      <span className="truncate">{entry.targetType.replace(/_/g, ' ')}</span>
                      <StatusPill status={entry.actorRole.replace('company_', '')} variant={roleVariant(entry.actorRole)} />
                    </div>
                  </div>

                  {/* Target */}
                  <div className="hidden min-w-0 truncate text-xs text-text-secondary @2xl:block">
                    <span>{entry.targetType.replace(/_/g, ' ')}</span>
                    {entry.targetUid && (
                      <span className="ml-1.5 font-mono text-text-muted">
                        ···{entry.targetUid.slice(-8)}
                      </span>
                    )}
                  </div>

                  {/* Role */}
                  <div className="hidden @2xl:block">
                    <StatusPill
                      status={entry.actorRole.replace('company_', '')}
                      variant={roleVariant(entry.actorRole)}
                    />
                  </div>

                  {/* Expand toggle */}
                  <div className="flex justify-center text-text-muted">
                    {expanded === entry._id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </div>
                </button>

                {/* Expanded detail */}
                {expanded === entry._id && <ExpandedRow entry={entry} />}
              </div>
            ))}
          </div>
        )}

        {data && !loadError && data.total > data.limit && (
          <div className="border-t border-border px-5">
            <Pagination
              page={data.page}
              total={data.total}
              limit={data.limit}
              onChange={(p) => { setPage(p); setExpanded(null); }}
            />
          </div>
        )}
      </SettingsCard>
      </div>
    </SettingsPage>
  );
}
